import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, useTheme } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Building2, Key, FileText, Wrench, Users, Moon, Sun, ChevronRight, Globe } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const languages = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "nl", name: "Nederlands", flag: "🇳🇱" },
  { code: "es", name: "Español", flag: "🇪🇸" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [registerName, setRegisterName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");

  useEffect(function() { if (user) navigate("/dashboard"); }, [user, navigate]);

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true);
    try {
      var response = await fetch(API_URL + "/api/auth/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: loginEmail, password: loginPassword }) });
      var data = await response.json();
      if (response.ok) { login(data, data.token); toast.success(t("auth.welcomeBack")); navigate("/dashboard"); }
      else { toast.error(data.detail || t("common.error")); }
    } catch (err) { toast.error(t("common.error")); }
    finally { setLoading(false); }
  }

  async function handleRegister(e) {
    e.preventDefault();
    setLoading(true);
    try {
      var response = await fetch(API_URL + "/api/auth/register", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: registerName, email: registerEmail, password: registerPassword }) });
      var data = await response.json();
      if (response.ok) { login(data, data.token); toast.success(t("auth.accountCreated")); navigate("/dashboard"); }
      else { toast.error(data.detail || t("common.error")); }
    } catch (err) { toast.error(t("common.error")); }
    finally { setLoading(false); }
  }

  function handleGoogleLogin() {
    var redirectUrl = window.location.origin + "/dashboard";
    window.location.href = "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  }

  function handleLanguageChange(langCode) { i18n.changeLanguage(langCode); }

  var currentLang = i18n.language || "en";
  var currentLangData = languages.find(function(l) { return l.code === currentLang; }) || languages[0];

  var features = [
    { icon: Building2, title: t("features.propertyManagement"), desc: t("features.propertyManagementDesc") },
    { icon: Key, title: t("features.keyManagement"), desc: t("features.keyManagementDesc") },
    { icon: FileText, title: t("features.documentsContracts"), desc: t("features.documentsContractsDesc") },
    { icon: Wrench, title: t("features.serviceRequests"), desc: t("features.serviceRequestsDesc") },
    { icon: Users, title: t("features.multiUserAccess"), desc: t("features.multiUserAccessDesc") },
  ];

  if (user) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="fixed top-0 left-0 right-0 z-50 border-b bg-background/80 backdrop-blur-lg">
        <div className="container mx-auto px-6 h-32 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_bd8042f0-b1af-4ffb-b95c-6b13c2cafb43/artifacts/citl3dkd_logo_basic.png" alt="Real Estate Manager" className="h-28 w-auto" />
          </div>
          <div className="flex items-center gap-2">
            <Select value={currentLang} onValueChange={handleLanguageChange}>
              <SelectTrigger className="w-auto gap-2" data-testid="landing-language-select">
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{currentLangData.flag} {currentLangData.name}</span>
                <span className="sm:hidden">{currentLangData.flag}</span>
              </SelectTrigger>
              <SelectContent>
                {languages.map(function(lang) { return <SelectItem key={lang.code} value={lang.code}><span className="mr-2">{lang.flag}</span>{lang.name}</SelectItem>; })}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">{theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}</Button>
          </div>
        </div>
      </header>

      <main className="pt-16">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src="https://images.unsplash.com/photo-1663756915301-2ba688e078cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzcwMTQ0NTE1fDA&ixlib=rb-4.1.0&q=85" alt="Modern interior" className="w-full h-full object-cover opacity-10 dark:opacity-5" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background" />
          </div>

          <div className="relative z-10 container mx-auto px-6 py-20 lg:py-32">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8 animate-fade-in">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />{t("landing.platformBadge")}
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">{t("landing.title")}</h1>
                <p className="text-lg text-muted-foreground max-w-lg">{t("landing.subtitle")}</p>
                <div className="flex flex-wrap gap-4">
                  {features.slice(0, 3).map(function(feature, idx) {
                    return <div key={idx} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted/50 border"><feature.icon className="w-4 h-4 text-primary" /><span className="text-sm font-medium">{feature.title}</span></div>;
                  })}
                </div>
              </div>

              <div className="lg:ml-auto w-full max-w-md animate-fade-in" style={{ animationDelay: "0.1s" }}>
                <Card className="border-2" data-testid="auth-card">
                  <CardHeader className="text-center pb-2">
                    <CardTitle className="text-2xl">{t("auth.getStarted")}</CardTitle>
                    <CardDescription>{t("auth.signInOrCreate")}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Button variant="outline" className="w-full h-12 gap-3" onClick={handleGoogleLogin} data-testid="google-login-btn">
                      <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" /><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>
                      {t("auth.continueWithGoogle")}
                    </Button>

                    <div className="relative"><div className="absolute inset-0 flex items-center"><div className="w-full border-t" /></div><div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">{t("auth.orContinueWithEmail")}</span></div></div>

                    <Tabs defaultValue="login" className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="login" data-testid="login-tab">{t("auth.login")}</TabsTrigger>
                        <TabsTrigger value="register" data-testid="register-tab">{t("auth.register")}</TabsTrigger>
                      </TabsList>
                      <TabsContent value="login" className="space-y-4 mt-4">
                        <form onSubmit={handleLogin} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="login-email" className="form-label">{t("common.email")}</Label>
                            <Input id="login-email" type="email" placeholder="name@example.com" value={loginEmail} onChange={function(e) { setLoginEmail(e.target.value); }} required data-testid="login-email-input" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="login-password" className="form-label">{t("common.password")}</Label>
                            <Input id="login-password" type="password" placeholder="••••••••" value={loginPassword} onChange={function(e) { setLoginPassword(e.target.value); }} required data-testid="login-password-input" />
                          </div>
                          <Button type="submit" className="w-full h-11" disabled={loading} data-testid="login-submit-btn">{loading ? t("auth.signingIn") : t("auth.signIn")}<ChevronRight className="w-4 h-4 ml-2" /></Button>
                        </form>
                      </TabsContent>
                      <TabsContent value="register" className="space-y-4 mt-4">
                        <div className="text-center space-y-4">
                          <p className="text-sm text-muted-foreground">{t("auth.startManagingDesc")}</p>
                          <Button onClick={function() { navigate("/register"); }} className="w-full h-11 bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="start-register-btn">
                            {t("auth.startManageRealestate")}<ChevronRight className="w-4 h-4 ml-2" />
                          </Button>
                          <p className="text-xs text-muted-foreground">{t("auth.trialInfo")}</p>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold tracking-tight mb-4">{t("landing.everythingYouNeed")}</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">{t("landing.comprehensiveTools")}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {features.map(function(feature, idx) {
                return (
                  <div key={idx} className="bento-card text-center animate-fade-in" style={{ animationDelay: (idx * 0.1) + "s" }}>
                    <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary/10 flex items-center justify-center"><feature.icon className="w-6 h-6 text-primary" /></div>
                    <h3 className="font-semibold mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <footer className="py-8 border-t"><div className="container mx-auto px-6 text-center text-sm text-muted-foreground"><p>{t("landing.footer")}</p></div></footer>
      </main>
    </div>
  );
}
